import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ServiceIcon from './ServiceIcon';

const Services: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const services = [
    {
      icon: '/images/pictograma_apps.png',
      title: 'Apps',
      description: 'Mais que aplicações, desenvolvemos experiências digitais para dispositivos móveis.',
    },
    {
      icon: '/images/pictograma_webapps.png',
      title: 'Web apps',
      description: 'Criamos sites com layouts adaptáveis à diversas telas aprimorando a experiência do usuário',
    },
    {
      icon: '/images/pictograma_webpage.png',
      title: 'Web page',
      description: 'Materializamos análises de mercado, tendências e usabilidade em pages para o seu negócio.',
    },
    {
      icon: '/images/pictograma_sistemas.png',
      title: 'Sistemas',
      description: 'Desenvolvemos e implementamos sistemas de informação e soluções tecnológicas eficientes.',
    },
    {
      icon: '/images/pictograma_ux.png',
      title: 'UX/UI',
      description: 'Antecipamos as necessidades do usuário e garantimos que as interfaces forneçam uma experiência confortável e intuitiva.',
    },
    {
      icon: '/images/pictograma_consultoria.png',
      title: 'Consultoria',
      description: 'Identificamos no seu negócio as principais necessidades em TI, UI e UX.',
    },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 lg:py-32 bg-gradient-to-b from-slate-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"></div>
      </div>
      
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-24"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : { scale: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-8"
          >
            ✨ Nossos Serviços
          </motion.div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-8 leading-tight">
            O que <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 bg-clip-text text-transparent">fazemos</span>
          </h2>
          <div className="w-32 h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 mx-auto mb-10 rounded-full"></div>
          <p className="text-xl md:text-2xl text-gray-600 max-w-5xl mx-auto leading-relaxed font-light">
            Criamos experiências digitais a partir de uma visão holística, com posicionamento estratégico 
            sem perder o foco do usuário final.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 lg:gap-12 mb-24">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 60, scale: 0.9 }}
              transition={{ duration: 0.7, delay: index * 0.2 }}
              className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 text-center 
                         shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 
                         border border-white/50 hover:border-blue-200/50 overflow-hidden"
            >
              {/* Card background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 
                             opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 via-purple-100 to-blue-200
                                 rounded-3xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3
                                 transition-all duration-500 shadow-lg group-hover:shadow-xl">
                    <ServiceIcon
                      src={service.icon}
                      alt={`${service.title} - FaliFe Tecnologia`}
                      title={service.title}
                    />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 
                             group-hover:text-blue-700 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg font-medium">
                  {service.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.9 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="text-center"
        >
          <button
            onClick={scrollToContact}
            className="group relative bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 
                       hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 
                       text-white font-black text-xl px-16 py-6 rounded-full 
                       transition-all duration-500 transform hover:scale-110 hover:shadow-2xl 
                       shadow-xl border-2 border-white/20 overflow-hidden"
          >
            <span className="relative z-10">✨ Solicite um orçamento</span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent 
                           opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
